import { useState } from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import SelectButton from "common/components/ui/SelectButton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { TIMEZONES } from "../constants";

import type { Timezone } from "../types";

interface TimezoneSelectProps {
  className: string;
  selectedTimezone: Timezone;
  onSelect: (newTimezone: Timezone) => void;
  isDisabled: boolean;
  isScheduling: boolean;
}

export default function TimezoneSelect({
  className,
  selectedTimezone,
  onSelect,
  isDisabled,
  isScheduling,
}: TimezoneSelectProps) {
  const isDesktop = useTwBreakpoint("lg");

  const [inputValue, setInputValue] = useState("");

  const lowercasedInput = inputValue.toLowerCase();
  const filteredOptions = TIMEZONES.filter(
    ({ label, offset }) =>
      label.toLowerCase().includes(lowercasedInput) &&
      (isScheduling ? !!offset : true),
  );

  const timezoneOption = TIMEZONES.find(
    ({ value }) => value === selectedTimezone.name,
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={isDisabled}>
        <SelectButton
          className={className}
          id="timezone-select"
          hasSelection={!!selectedTimezone}
          hasError={!timezoneOption}
        >
          {timezoneOption ? timezoneOption.label : "Invalid timezone selected"}
        </SelectButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-md:max-w-[var(--radix-dropdown-menu-trigger-width)]"
        align={isDesktop ? "end" : "start"}
      >
        <MenuSearchInput
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        <DropdownMenuList>
          {filteredOptions.map(({ label, value, offset }) => (
            <DropdownMenuItem
              key={value}
              onSelect={() => {
                onSelect({ name: value, offset });
              }}
            >
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
